import { useEffect, useState } from 'react';
import type { MediaSummary } from '@familienbaum/shared';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/misc';
import { ScanFaces } from '@/components/media/scan-faces';

/** Ein hochgeladenes Bild samt der Datei, aus der es entstanden ist. */
export interface NameFacesItem {
  media: MediaSummary;
  /**
   * Die Datei vom Gerät.
   *
   * Gerechnet und zugeschnitten wird darauf und nicht auf den Ableitungen vom
   * Server: die entstehen erst im Hintergrund, und bis dahin gäbe es nichts zu
   * sehen. Die Markierungen sind auf 0…1 normiert und passen deshalb auf
   * beide.
   */
  file: File;
}

/**
 * Gesichter benennen – für einen ganzen Stapel, Bild für Bild.
 *
 * Aus der Galerie kommen mehrere Bilder auf einmal, ohne Zuschnitt-Schritt.
 * Ohne diesen Dialog endete der Vorgang dort bei „hochgeladen", und die
 * Gesichter blieben liegen – während der Weg über die Kamera direkt ins
 * Benennen führte. Zwei Wege ins selbe Archiv, von denen nur einer weiterhalf.
 *
 * Der Stapel wird nacheinander abgearbeitet: „Nächstes Bild" geht weiter,
 * „Später benennen" bricht den ganzen Stapel ab. Was schon benannt ist, bleibt
 * benannt – jeder Handgriff ist für sich gespeichert.
 */
export function NameFacesDialog({
  items,
  open,
  onOpenChange,
}: {
  items: NameFacesItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [index, setIndex] = useState(0);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  /** Der Browser kann dieses Format nicht anzeigen (HEIC etwa). */
  const [unreadable, setUnreadable] = useState(false);

  const current = items[index] ?? null;

  // Beim Öffnen eines neuen Stapels vorne anfangen.
  useEffect(() => {
    if (open) setIndex(0);
  }, [open, items]);

  /**
   * Das Bild des aktuellen Schritts laden. Die Objekt-URL wird beim Wechsel
   * wieder freigegeben – sonst hält der Browser jedes Bild des Stapels bis
   * zum Neuladen der Seite im Speicher.
   */
  useEffect(() => {
    if (!open || !current) return;
    setImage(null);
    setUnreadable(false);

    const url = URL.createObjectURL(current.file);
    let cancelled = false;
    const node = new Image();
    node.onload = () => {
      if (!cancelled) setImage(node);
    };
    node.onerror = () => {
      if (!cancelled) setUnreadable(true);
    };
    node.src = url;

    return () => {
      cancelled = true;
      URL.revokeObjectURL(url);
    };
  }, [open, current]);

  const next = () => {
    if (index + 1 < items.length) setIndex(index + 1);
    else onOpenChange(false);
  };

  if (!current) return null;
  const last = index + 1 >= items.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        tone="ink"
        title="Wer ist auf dem Bild?"
        description={
          items.length > 1
            ? `${items.length} Bilder sind angekommen – benennen geht jetzt am schnellsten.`
            : undefined
        }
      >
        {unreadable ? (
          // Kein Grund für eine Fehlermeldung: die Datei liegt im Archiv, nur
          // anzeigen kann der Browser sie nicht. Also weiter im Stapel.
          <div className="flex flex-col gap-4 py-2">
            <p className="text-[13px] leading-relaxed text-panel-ink-2">
              Dieses Bildformat kann der Browser nicht anzeigen – Gesichter
              lassen sich hier nicht markieren. Im Archiv liegt es trotzdem.
            </p>
            <button
              type="button"
              onClick={next}
              className="h-11 rounded-full bg-accent text-[14px] font-semibold text-accent-ink"
            >
              {last ? 'Fertig' : 'Nächstes Bild'}
            </button>
          </div>
        ) : image ? (
          <ScanFaces
            key={current.media.id}
            media={current.media}
            image={image}
            onClose={() => onOpenChange(false)}
            onNext={next}
            nextLabel={last ? 'Fertig' : 'Nächstes Bild'}
            position={items.length > 1 ? { index, total: items.length } : undefined}
          />
        ) : (
          <p className="flex items-center gap-2.5 py-8 text-[13px] text-panel-ink-2">
            <Spinner className="size-4 text-accent" />
            Bild wird geladen …
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
